/**
 * Iter 58.5 — Labour Headcount Dashboard.
 *
 * Today's live workforce snapshot: total / present / absent / off-radius, plus
 * site-wise, trade-wise, gender, and designation breakdowns.
 */
import React, { useEffect, useState } from 'react';
import { Users, MapPin, AlertTriangle, ScanFace, CheckCircle2, Clock } from 'lucide-react';
import PageHeader from '../components/PageHeader';
import { apiGet } from '../lib/api';

export default function LabourDashboard() {
  const [data, setData] = useState(null);
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [subcontractor, setSubcontractor] = useState('');
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ date_str: date });
      if (subcontractor.trim()) params.set('subcontractor_name', subcontractor.trim());
      const r = await apiGet(`/labour-master/dashboard?${params.toString()}`);
      setData(r);
    } catch { /* silent */ } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [date, subcontractor]);

  const t = data?.totals || {};

  return (
    <div>
      <PageHeader
        overline="Workforce"
        title="Labour Dashboard"
        subtitle="Live headcount across sites — present vs absent, trade mix, off-radius alerts, and face-enrolment coverage."
      />

      {/* Filters */}
      <div className="bg-white border border-slate-200 rounded-sm p-3 mb-4 flex flex-wrap items-end gap-3" data-testid="labour-dash-filters">
        <div>
          <label className="text-[10px] uppercase tracking-widest text-slate-500 font-bold block mb-1">Date</label>
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="border border-slate-200 rounded-sm text-sm px-3 py-2" data-testid="dash-date" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-slate-500 font-bold block mb-1">Subcontractor</label>
          <input
            value={subcontractor}
            onChange={(e) => setSubcontractor(e.target.value)}
            placeholder="Exact name…"
            className="border border-slate-200 rounded-sm text-sm px-3 py-2 bg-white w-44"
            data-testid="dash-subcontractor"
          />
        </div>
      </div>

      {/* KPI Strip */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-7 gap-3 mb-6" data-testid="labour-kpis">
        <KPI icon={Users} label="Total Workforce" value={t.total ?? 0} sub={t.inactive_count ? `${t.inactive_count} inactive` : undefined} />
        <KPI icon={CheckCircle2} label="Present Today" value={t.present ?? 0} accent="emerald" />
        <KPI icon={Users} label="Absent" value={t.absent ?? 0} accent="slate" />
        <KPI icon={AlertTriangle} label="Off-Radius" value={t.off_radius ?? 0} accent="rose" />
        <KPI icon={MapPin} label="Attendance" value={`${t.attendance_pct ?? 0}%`} accent="indigo" sub="Active labour only" />
        <KPI icon={ScanFace} label="Face Enrolled" value={t.face_enrolled ?? 0} accent="amber" />
        <KPI icon={Clock} label="Man-Hours" value={`${t.man_hours ?? 0}h`} accent="indigo" />
      </div>

      {loading && !data && <div className="text-center py-12 text-sm text-slate-400">Loading…</div>}

      {data && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {/* Site-wise */}
          <Breakdown title="Site-wise" rows={data.by_site} columns={['name', 'total', 'present', 'absent', 'off_radius', 'attendance_pct']} headers={['Site', 'Total', 'Present', 'Absent', 'Off-rad', '%']} testid="dash-by-site" />
          {/* Trade-wise */}
          <Breakdown title="Trade / Skill-wise" rows={data.by_trade} columns={['name', 'total', 'present', 'absent']} headers={['Trade', 'Total', 'Present', 'Absent']} testid="dash-by-trade" />
          {/* Gender-wise */}
          <Breakdown title="Gender mix" rows={data.by_gender} columns={['name', 'total', 'present', 'absent']} headers={['Gender', 'Total', 'Present', 'Absent']} testid="dash-by-gender" />
          {/* Designation-wise */}
          <Breakdown title="Designation" rows={data.by_designation} columns={['name', 'total', 'present', 'absent']} headers={['Designation', 'Total', 'Present', 'Absent']} testid="dash-by-designation" />
          {/* Subcontractor-wise */}
          <Breakdown title="Subcontractor" rows={data.by_subcontractor} columns={['name', 'total', 'present', 'absent']} headers={['Subcontractor', 'Total', 'Present', 'Absent']} testid="dash-by-subcontractor" />
        </div>
      )}

      {data && data.totals.total === 0 && (
        <div className="mt-8 text-center bg-white border border-dashed border-slate-200 rounded-sm py-12 text-sm text-slate-500">
          No labour records yet. <a href="/app/labour-master" className="text-[#1F7A6E] font-semibold underline">Import from Excel →</a>
        </div>
      )}
    </div>
  );
}

function KPI({ icon: Icon, label, value, accent, sub }) {
  const color = {
    emerald: 'text-emerald-700 bg-emerald-50',
    rose: 'text-rose-700 bg-rose-50',
    indigo: 'text-[#1F7A6E] bg-[#1F7A6E]/10',
    amber: 'text-amber-700 bg-amber-50',
    slate: 'text-slate-700 bg-slate-50',
  }[accent] || 'text-slate-700 bg-slate-50';
  return (
    <div className="bg-white border border-slate-200 rounded-sm p-4">
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">{label}</div>
        <div className={`w-7 h-7 rounded ${color} flex items-center justify-center`}><Icon size={14} /></div>
      </div>
      <div className="font-display font-bold text-2xl text-slate-900 mt-1.5">{value}</div>
      {sub && <div className="text-[10px] text-slate-400 mt-0.5">{sub}</div>}
    </div>
  );
}

function Breakdown({ title, rows = [], columns, headers, testid }) {
  return (
    <div className="bg-white border border-slate-200 rounded-sm" data-testid={testid}>
      <div className="px-4 py-2.5 border-b border-slate-100 flex items-center justify-between">
        <div className="font-display font-bold text-sm uppercase tracking-wider text-slate-700">{title}</div>
        <div className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">{rows.length} entries</div>
      </div>
      <div className="overflow-x-auto">
        <div className="overflow-y-auto" style={{ maxHeight: '280px' }}>
          <table className="w-full dense-table text-xs">
            <thead><tr>{headers.map((h, i) => <th key={i} className={i === 0 ? '' : 'text-right'}>{h}</th>)}</tr></thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan={columns.length} className="text-center text-slate-400 py-6">No data</td></tr>}
              {rows.map((r, i) => (
                <tr key={i}>
                  {columns.map((c, j) => (
                    <td key={c} className={j === 0 ? 'font-semibold' : 'text-right tabular-nums'}>
                      {c === 'attendance_pct' ? `${r[c] ?? 0}%` : (r[c] ?? '—')}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
